import React, { useContext, useEffect, useState } from 'react';
import { CustomDialogContainer, CustomDialogTitle } from '../../../Common/dialog';
import { mergeSx, Styles } from '@t2c/shared-fe';
import DialogContent from '@mui/material/DialogContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { AI_GENERATION_STAGE, AIMediaStatus } from '../../../@types';
import { useAiGenerate, useDialog } from '../../../hooks';
import TextField from '@mui/material/TextField';
import Skeleton from '@mui/material/Skeleton';
import {
  AiVideoModelEnum,
  EXTENDED_ASPECT_RATIO_OPTIONS,
  NumGenerationsEnum,
  ResolutionEnum,
  showCredits,
} from '../../../utils';
import { Iconify, NextImage } from '@sdf/base';
import CustomizedProgressBars from '../../../Common/ProgressBar';
import AvatarWithLoader from '../../../Common/AvatarWithLoader';
import VideoWithLoader from '../../../Common/VideoWithLoader';
import { Box, Checkbox, Grid, MenuItem, Select, Tooltip } from '@mui/material';
import { ProgressStuckNote } from '../../../Common/ProgressStuckNote';
import { FileUploader } from 'react-drag-drop-files';
import { useSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import { AspectRatios } from '@t2c/ai-video-editor';
import { UploadImage } from '../../broll/UploadImage';
import { BrowseBRollModal } from '../../edit-video/modals/BrowseBRoll/BrowseBRollModal';
import { CreditContext } from 'apps/t2c-fe/context/CreditProvider';

interface Props {
  shouldOpenDialog: boolean;
  handleOnCloseDialog: () => void;
  isImage: boolean;
  handleApplyBroll: (mediaUrl?: string, thumbnailUrl?: string) => void;
  fetchAiMediaData: () => void;
  shouldHideApplyButton?: boolean;
}

export const AiGeneratedMediaDialog: React.FC<Props> = ({
  shouldOpenDialog,
  handleOnCloseDialog,
  isImage,
  handleApplyBroll,
  fetchAiMediaData,
  shouldHideApplyButton,
}) => {
  const {
    currentStage,
    prompt,
    mediaName,
    clipGenerationData,
    setClipGenerationData,
    uploadURL,
    setUploadURL,
    handleDeselectImage,
    handleClipDataChange,
    handlePromptChange,
    handleGenerateClick,
    handleMinimizeClick,
    handleContinueGeneration,
    handleReGenerateClick,
    handleOnApply,
    handleCloseAiGenDialog,
    handleMediaNameChange,
    isFetchingRequiredCredits,
    requiredCreditsData,
    dialogTitle,
    genAiMediaData,
    handleOnConfirmGeneration,
    usableCredits,
    handleRedirectToPricingPage,
    checked,
    handleCheckboxChange,
    isCreditFetchError,
    progress,
    setUploadTailURL,
    handleDeselectTailImage,
    handleImageSelect,
    uploadTailURL,
    setIsSelectedTail,
  } = useAiGenerate({
    isImage,
    handleApplyBroll,
    handleOnCloseDialog,
    fetchAiMediaData,
    shouldOpenDialog,
  });
  const { modelDetails } = useContext(CreditContext);

  const [maxCharLimit, setMaxCharLimit] = useState<number>(3500);
  const supportedFormats = ['jpg', 'jpeg', 'png'];
  const { enqueueSnackbar } = useSnackbar();
  const isInQueue =
    genAiMediaData?.status === AIMediaStatus.NotStarted ||
    genAiMediaData?.status === AIMediaStatus.InQueue;

  const {
    isDialogOpen: isOpenBrowseBRollModal,
    openDialog: openBrowseBRollModal,
    closeDialog: CloseBrowseBRollModal,
  } = useDialog();

  const handleClickStartImage = () => {
    setIsSelectedTail(false);
    openBrowseBRollModal();
  };
  const handleClickEndImage = () => {
    setIsSelectedTail(true);
    openBrowseBRollModal();
  };

  useEffect(() => {
    if (clipGenerationData.model) {
      setClipGenerationData((prev) => ({
        ...prev,
        duration: modelDetails[clipGenerationData.model].durationPerModel[0] ?? 5,
        orientation:
          modelDetails[clipGenerationData.model].orientationPerModel[0] ?? AspectRatios['16_9'],
        uploadId: '',
        uploadTailId: '',
      }));
      setUploadURL('');
      setUploadTailURL('');
      const maxchar = modelDetails[clipGenerationData.model].maxCharLimit;
      setMaxCharLimit(maxchar);
      if (prompt.length > maxchar) {
        handlePromptChange(prompt.slice(0, maxchar));
      }
    }
  }, [clipGenerationData.model]);

  return (
    <CustomDialogContainer open={shouldOpenDialog} maxWidth="md">
      <CustomDialogTitle title={dialogTitle} onClose={handleCloseAiGenDialog} sx={styles.title} />
      <DialogContent sx={styles.dialogContent}>
        {currentStage === AI_GENERATION_STAGE.PROMPT_INPUT && isImage && (
          <Stack rowGap={2}>
            <Typography sx={styles.h6}>
              What type of {isImage ? 'image' : 'video'} would you like to create?
            </Typography>
            <TextField
              placeholder="Type your prompt here!"
              size="medium"
              type="text"
              name="prompt"
              multiline
              rows={4}
              sx={styles.textField}
              value={prompt}
              onChange={(e) => handlePromptChange(e.target.value)}
            />

            <TextField
              placeholder="Name"
              size="small"
              type="text"
              name="mediaName"
              sx={styles.textField}
              value={mediaName}
              onChange={(e) => handleMediaNameChange(e.target.value)}
            />
            <Stack rowGap={2} mt={4}>
              <Stack sx={styles.promptExamplesContainer}>
                <Typography sx={styles.subText}>Here are some prompt examples:</Typography>
                <Typography sx={styles.promptExamples}>
                  1. A person making a phone call near a busy road with heavy traffic.
                  <br />
                  2. Water flowing through the streets of New York.
                </Typography>
              </Stack>
            </Stack>
            <Stack sx={styles.promptExamplesContainer}>
              <Typography sx={styles.subText}>Note:</Typography>
              <Typography sx={styles.promptExamples}>
                Prompt will fail for offensive language and explicit content. Please avoid sharing
                personal or sensitive information.
              </Typography>
            </Stack>

            <Stack direction="row" justifyContent={'space-between'} mt={4}>
              <Button variant="outlined" sx={styles.cancelBtn} onClick={handleCloseAiGenDialog}>
                Cancel
              </Button>
              <Button variant="contained" sx={styles.generateBtn} onClick={handleGenerateClick}>
                Generate
              </Button>
            </Stack>
          </Stack>
        )}

        {currentStage === AI_GENERATION_STAGE.PROMPT_INPUT && !isImage && (
          <Grid container gap={3}>
            <Grid item xs={12}>
              <Typography sx={styles.heading}>
                What type of AI video clip would you like to create?
              </Typography>
              <TextField
                placeholder="E.g., Vibrant cityscape with lit skyscrapers, busy streets, and glowing neon signs."
                size="medium"
                type="text"
                name="prompt"
                multiline
                inputProps={{ maxLength: maxCharLimit }}
                rows={4}
                sx={styles.textField}
                onChange={(e) => handlePromptChange(e.target.value?.slice(0, maxCharLimit))}
                value={prompt}
                fullWidth
              />
              <Box>
                <Typography
                  variant="body2"
                  sx={{ color: (theme) => theme.palette.text.placeholder, pl: '2px', pt: '2px' }}
                >
                  {prompt?.length || 0}/{maxCharLimit} characters
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Stack>
                    <Typography sx={styles.label}>Enter Name</Typography>
                    <TextField
                      placeholder="Type name here"
                      size="medium"
                      type="text"
                      name="name"
                      sx={styles.textField}
                      value={mediaName}
                      onChange={(e) => handleMediaNameChange(e.target.value)}
                    />
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box flex={1}>
                    <Typography sx={styles.label}>Model</Typography>
                    <Select
                      labelId="model-select-label"
                      value={clipGenerationData.model}
                      onChange={(e) =>
                        handleClipDataChange('model', e.target.value as AiVideoModelEnum)
                      }
                      sx={{
                        width: '100%',
                        backgroundColor: (theme) => theme.palette.background.secondary,
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: styles.dropdownProps,
                        },
                      }}
                      renderValue={(selected) => selected}
                    >
                      {Object.values(AiVideoModelEnum).map((output) => (
                        <MenuItem key={output} value={output}>
                          <Box>
                            <Box>{output}</Box>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Stack direction={'row'} spacing={1}>
                    <Box flex={1}>
                      <Typography sx={styles.label}>Generations</Typography>
                      <Select
                        labelId="numGeneration-select-label"
                        value={clipGenerationData.numGenerations}
                        onChange={(e) =>
                          handleClipDataChange(
                            'numGenerations',
                            e.target.value as NumGenerationsEnum
                          )
                        }
                        sx={{
                          width: '100%',
                          backgroundColor: (theme) => theme.palette.background.secondary,
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: styles.dropdownProps,
                          },
                        }}
                      >
                        {Object.values(NumGenerationsEnum).map((output) => (
                          <MenuItem key={output} value={output}>
                            {output}
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>
                    <Box flex={1}>
                      <Typography sx={styles.label}>Duration(in sec)</Typography>
                      <Select
                        labelId="duration-select-label"
                        value={clipGenerationData.duration}
                        onChange={(e) =>
                          handleClipDataChange('duration', e.target.value.toString())
                        }
                        sx={{
                          width: '100%',
                          backgroundColor: (theme) => theme.palette.background.secondary,
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: styles.dropdownProps,
                          },
                        }}
                      >
                        {modelDetails[clipGenerationData.model].durationPerModel.map((output) => (
                          <MenuItem key={output} value={output}>
                            {output}
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Stack direction={'row'} spacing={1}>
                    <Box flex={1}>
                      <Typography sx={styles.label}>Orientation</Typography>
                      <Select
                        labelId="orientation-select-label"
                        value={clipGenerationData.orientation}
                        onChange={(e) => handleClipDataChange('orientation', e.target.value)}
                        sx={{
                          width: '100%',
                          backgroundColor: (theme) => theme.palette.background.secondary,
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: styles.dropdownProps,
                          },
                        }}
                      >
                        {EXTENDED_ASPECT_RATIO_OPTIONS.filter((option) =>
                          modelDetails[clipGenerationData.model].orientationPerModel.includes(
                            option.value
                          )
                        ).map(({ id, value, icon, label, name }) => (
                          <MenuItem key={id} value={value} sx={styles.menuItem}>
                            <Stack direction="row" alignItems="center" gap={1}>
                              <Box sx={{ width: 35, display: 'flex', justifyContent: 'center' }}>
                                {icon}
                              </Box>
                              <Stack direction="row" alignItems="center" gap={1}>
                                <Typography variant="body2" sx={styles.menuLabel}>
                                  {label} {name}
                                </Typography>
                              </Stack>
                            </Stack>
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>
                    <Box flex={1}>
                      <Typography sx={styles.label}>Resolution</Typography>
                      <Select
                        labelId="resolution-select-label"
                        value={clipGenerationData.resolution}
                        onChange={(e) =>
                          handleClipDataChange('resolution', e.target.value as ResolutionEnum)
                        }
                        sx={{
                          width: '100%',
                          backgroundColor: (theme) => theme.palette.background.secondary,
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: styles.dropdownProps,
                          },
                        }}
                      >
                        {Object.values(ResolutionEnum).map((output) => (
                          <MenuItem key={output} value={output}>
                            {output}
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>
                  </Stack>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} order={{ xs: 2, sm: 1 }}>
                  <Box mt={1.5} sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                    {/* {modelDetails[clipGenerationData.model].showUpload && (
                  <Box>
                    <Typography sx={styles.text}>Start Image</Typography>
                    <UploadImage
                      uploadURL={uploadURL}
                      openBrowseBRollModal={handleClickStartImage}
                      handleDeselectImage={handleDeselectImage}
                    />
                  </Box>
                )}
                {modelDetails[clipGenerationData.model].showTailUpload && (
                  <Box>
                    <Typography sx={styles.text}>End Image</Typography>
                    <UploadImage
                      uploadURL={uploadTailURL}
                      openBrowseBRollModal={handleClickEndImage}
                      handleDeselectImage={handleDeselectTailImage}
                    />
                  </Box>
                )} */}
                  </Box>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  order={{ xs: 1, sm: 2 }}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end', // push content to bottom vertically
                  }}
                >
                  <Box mt={2}>
                    <Button
                      variant="contained"
                      fullWidth
                      sx={styles.generateBtn}
                      endIcon={
                        <NextImage src={'/assets/icons/Ai.svg'} alt="" width={24} height={24} />
                      }
                      onClick={handleGenerateClick}
                    >
                      Generate
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}

        {currentStage === AI_GENERATION_STAGE.CREDITS_CHECK && (
          <Stack rowGap={4}>
            <Stack sx={styles.creditContainer}>
              <Typography sx={{ ...styles.boldText, display: 'flex', alignItems: 'center' }}>
                Credits Available:{' '}
                {isFetchingRequiredCredits ? (
                  <Skeleton width={50} sx={{ display: 'inline-block', mx: 1 }} />
                ) : (
                  showCredits(usableCredits > 0 ? usableCredits : 0)
                )}
              </Typography>
              <Typography sx={{ ...styles.grayText, display: 'flex', alignItems: 'center' }}>
                This action will cost{' '}
                {isFetchingRequiredCredits ? (
                  <Skeleton width={50} sx={{ display: 'inline-block', mx: 1 }} />
                ) : (
                  showCredits(requiredCreditsData?.requiredCredits)
                )}{' '}
                credits.
              </Typography>
            </Stack>
            {isCreditFetchError && !isFetchingRequiredCredits && (
              <Stack>
                <Typography color="error.main" textAlign={'center'}>
                  We couldn't fetch your credit data at the moment. Please try again.
                </Typography>
              </Stack>
            )}
            <Stack direction="row" alignItems="center" spacing={1}>
              <Checkbox
                checked={checked}
                onChange={handleCheckboxChange}
                inputProps={{ 'aria-label': 'controlled' }}
              />
              <Typography>Don't Show Again</Typography>
            </Stack>
            <Stack direction="row" justifyContent={'space-between'} mt={4}>
              <Button variant="outlined" sx={styles.cancelBtn} onClick={handleCloseAiGenDialog}>
                Cancel
              </Button>
              {isFetchingRequiredCredits ? (
                <Skeleton width={120} height={70} sx={{ borderRadius: 4 }} />
              ) : isCreditFetchError ? (
                <Button
                  variant="contained"
                  sx={styles.generateBtn}
                  onClick={handleGenerateClick}
                  disabled={isFetchingRequiredCredits}
                >
                  Retry
                </Button>
              ) : usableCredits < requiredCreditsData?.requiredCredits ? (
                <Button
                  variant="contained"
                  sx={styles.generateBtn}
                  onClick={handleRedirectToPricingPage}
                  disabled={isFetchingRequiredCredits}
                >
                  Upgrade Plan
                </Button>
              ) : (
                <Button
                  variant="contained"
                  sx={styles.generateBtn}
                  onClick={handleOnConfirmGeneration}
                  disabled={isFetchingRequiredCredits}
                >
                  Confirm
                </Button>
              )}
            </Stack>
          </Stack>
        )}

        {currentStage === AI_GENERATION_STAGE.GENERATING && (
          <Stack alignItems={'center'} rowGap={2}>
            {isImage || isInQueue ? (
              <CircularProgress />
            ) : (
              <CustomizedProgressBars value={progress || 0} sx={{ width: '80%' }} />
            )}
            <Typography sx={styles.boldText}>
              {isInQueue
                ? `Your ${
                    isImage ? 'image' : 'video'
                  } is in queue, getting closer to the spotlight!`
                : `Generating AI ${isImage ? 'image' : 'AI Video clip'}   `}
            </Typography>
            {!isImage && (
              <Typography sx={styles.grayText}>
                This may take a few minutes. You can minimize this screen and apply the generated
                clip later.
              </Typography>
            )}
            {!isImage && <ProgressStuckNote />}
            {!isImage && (
              <Stack direction="row" justifyContent={'flex-end'} width={'100%'} mt={2}>
                <Button variant="contained" sx={styles.generateBtn} onClick={handleMinimizeClick}>
                  Minimize
                </Button>
              </Stack>
            )}
          </Stack>
        )}

        {currentStage === AI_GENERATION_STAGE.ERROR && (
          <Stack alignItems={'center'} rowGap={4}>
            <Iconify
              icon="akar-icons:circle-alert"
              width="75px"
              height="75px"
              color={(theme) => theme.palette.error.main}
            />
            <Stack alignItems={'center'}>
              <Typography variant="h6" sx={styles.boldText}>
                {isImage ? 'Image' : 'Video'} Generation Process Failed
              </Typography>
              <Typography maxWidth={500} textAlign={'center'} sx={styles.grayText}>
                An unexpected system error occurred. Please try again.
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent={'space-between'} width={'100%'}>
              <Button variant="outlined" sx={styles.cancelBtn} onClick={handleCloseAiGenDialog}>
                Close
              </Button>
              <Button variant="contained" sx={styles.generateBtn} onClick={handleReGenerateClick}>
                Try Again
              </Button>
            </Stack>
          </Stack>
        )}

        {currentStage === AI_GENERATION_STAGE.VIDEO_PREVIEW && genAiMediaData && (
          <Stack>
            {isImage ? (
              <Stack alignItems={'center'} my={4}>
                <AvatarWithLoader
                  src={genAiMediaData?.url}
                  sx={{
                    borderRadius: 2,
                    height: 300,
                    width: 300,
                    objectFit: 'contain',
                  }}
                />
              </Stack>
            ) : (
              <Stack alignItems={'center'} my={4}>
                <VideoWithLoader
                  src={genAiMediaData?.url}
                  style={{
                    borderRadius: 8,
                    height: 250,
                    width: 400,
                    objectFit: 'cover',
                  }}
                  controls
                />
              </Stack>
            )}
            <Stack direction="row" justifyContent={'space-between'}>
              <Button variant="outlined" sx={styles.cancelBtn} onClick={handleCloseAiGenDialog}>
                Cancel
              </Button>
              <Stack direction="row" columnGap={1}>
                <Button variant="outlined" sx={styles.cancelBtn} onClick={handleReGenerateClick}>
                  Re-Generate
                </Button>
                {!shouldHideApplyButton && (
                  <Button variant="contained" sx={styles.generateBtn} onClick={handleOnApply}>
                    Apply
                  </Button>
                )}
              </Stack>
            </Stack>
          </Stack>
        )}

        {currentStage === AI_GENERATION_STAGE.CANCEL_CONFIRMATION && (
          <Stack rowGap={6}>
            <Typography textAlign={'center'} mt={6}>
              Are you sure you want to cancel AI video generation?
            </Typography>
            <Stack direction="row" justifyContent={'space-between'}>
              <Button variant="outlined" sx={styles.cancelBtn} onClick={handleCloseAiGenDialog}>
                Cancel
              </Button>
              <Button
                variant="contained"
                sx={styles.generateBtn}
                onClick={handleContinueGeneration}
              >
                Continue Generating
              </Button>
            </Stack>
          </Stack>
        )}
      </DialogContent>
      {isOpenBrowseBRollModal && (
        <BrowseBRollModal
          handleImageSelect={handleImageSelect}
          handleCloseDialog={CloseBrowseBRollModal}
          shouldOpenDialog={isOpenBrowseBRollModal}
          isImage={true}
          isBrollPage={true}
        />
      )}
    </CustomDialogContainer>
  );
};

const styles: Styles = {
  title: {
    paddingBottom: '10px',
    paddingTop: '10px',
  },
  h6: {
    fontWeight: 500,
    fontSize: 16,
  },
  subText: {
    fontWeight: 500,
    fontSize: 16,
  },
  dialogContent: {
    backgroundColor: (theme) => theme.palette.background.secondary,
  },
  heading: {
    fontSize: 14,
    fontWeight: 500,
    color: (theme) => theme.palette.text.primary,
    marginBottom: '5px',
    marginTop: '5px',
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      border: '1px solid',
      borderRadius: '6px',
      borderColor: (theme) => theme.palette.background.border,
      backgroundColor: (theme) => theme.palette.background.secondary,
      '&:hover': {
        borderColor: (theme) => theme.palette.background.activeBorder,
      },
    },
    '& .MuiOutlinedInput-input::placeholder': {
      color: (theme) => theme.palette.text.placeholder,
    },
  },
  label: {
    fontSize: 14,
    fontWeight: 400,
    color: (theme) => theme.palette.text.primary,
    marginBottom: '5px',
  },
  promptExamplesContainer: {
    backgroundColor: (theme) => theme.palette.background.activeCard,
    borderRadius: '6px',
    padding: 1,
  },
  promptExamples: {
    fontSize: '14px',
    lineHeight: 2.2,
    color: (theme) => theme.palette.text.primary,
  },
  cancelBtn: {
    color: (theme) => theme.palette.text.primary,
    borderColor: (theme) => theme.palette.background.border,
    borderRadius: '20px',
    fontWeight: '500',
    paddingX: 3,
  },
  generateBtn: {
    color: (theme) => theme.palette.text.primary,
    boxShadow: 'none',
    borderRadius: '20px',
    fontWeight: '500',
    paddingX: 4,
  },
  boldText: {
    fontWeight: 600,
  },
  grayText: {
    color: (theme) => theme.palette.text.placeholder,
  },
  creditInfoText: {
    color: (theme) => theme.palette.text.primary,
  },
  creditContainer: {
    backgroundColor: (theme) => theme.palette.background.activeCard,
    backgroundImage: "url('/assets/icons/UpgradeDiamondIcon.svg')",
    backgroundRepeat: 'no-repeat',
    backgroundSize: '6%',
    backgroundPosition: 'calc(100% - 10px) center',
    padding: 2,
    borderRadius: 1,
  },
  uploading: {
    position: 'relative',
    borderRadius: '10px',
    width: '210px',
  },
  uploader: {
    height: 100,
    width: 100,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    textAlign: 'center',
    borderStyle: 'dashed',
    borderRadius: '10px',
    flexDirection: 'column',
    borderColor: (theme) => theme.palette.background.border,
    backgroundColor: (theme) => theme.palette.background.secondary,
    cursor: 'pointer',
  },
  uploaderText: {
    color: (theme) => theme.palette.text.primary,
    fontSize: 16,
    fontWeight: 500,
  },
  dropdownProps: {
    bgcolor: (theme) => theme.palette.background.secondary,
    '& .MuiMenuItem-root': {
      backgroundColor: (theme) => theme.palette.background.secondary,
      '&:hover': {
        backgroundColor: (theme) => theme.palette.background.activeCard,
      },
    },
  },
};
